import { Space, Typography } from "antd";
import type { ReactNode } from "react";

const { Title, Text } = Typography;

export interface PageHeaderProps {
  /** Section icon — typically an `@ant-design/icons` element. */
  icon?: ReactNode;
  /** Tonal accent color used for the icon background. Defaults to brand orange. */
  accent?: string;
  /** Main heading. */
  title: ReactNode;
  /** Optional subtitle / supporting copy below the title. */
  subtitle?: ReactNode;
  /** Right-aligned action slot (buttons, dropdowns, etc.). */
  extra?: ReactNode;
  /** Additional bottom margin. Defaults to 16. */
  bottomGap?: number;
}

/**
 * Shared page header used at the top of every section.
 *
 * Layout: a tinted icon tile on the left, a bold title + faded subtitle on
 * the right, and an optional `extra` slot (e.g. a primary action button) at
 * the far end. The whole row sits flush above the page content with no card
 * background of its own — designed to be dropped right inside any `<Card>`
 * or directly on the page.
 */
export const PageHeader = ({
  icon,
  accent = "#FF8C00",
  title,
  subtitle,
  extra,
  bottomGap = 16,
}: PageHeaderProps) => {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 16,
        marginBottom: bottomGap,
        flexWrap: "wrap",
      }}
    >
      {icon && (
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: 12,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: `${accent}1A`, // ~10% opacity
            color: accent,
            fontSize: 24,
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
      )}

      <Space direction="vertical" size={2} style={{ flex: 1, minWidth: 0 }}>
        <Title level={3} style={{ margin: 0, lineHeight: 1.2 }}>
          {title}
        </Title>
        {subtitle && (
          <Text type="secondary" style={{ fontSize: 13 }}>
            {subtitle}
          </Text>
        )}
      </Space>

      {extra && <div style={{ marginLeft: "auto" }}>{extra}</div>}
    </div>
  );
};

export default PageHeader;
